import React, { memo, Fragment, type ReactNode } from 'react';
import type { PreloadedQuery } from 'react-relay';
import { SmartCardProvider } from '@atlaskit/link-provider';
import client from '@atlassian/jira-apollo-gira';
import { ApolloClientProvider } from '@atlassian/jira-apollo-multiple-clients/src/main.tsx';
import BusinessAppWrapper from '@atlassian/jira-business-app-wrapper/src/ui/app-wrapper/index.tsx';
import { ConfluenceModalProvider } from '@atlassian/jira-business-confluence-pages/src/ui/confluence-view-modal/index.tsx';
import { useTenantHasConfluence } from '@atlassian/jira-business-confluence-pages/src/utils/use-tenant-has-confluence/index.tsx';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import { IssueTypesAndFieldsContext } from '@atlassian/jira-business-entity-project/src/controllers/issue-types-and-fields-context/index.tsx';
import { RefetchFieldsController } from '@atlassian/jira-business-entity-project/src/controllers/refetch-fields/index.tsx';
import { ExperienceErrorBoundary } from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import { IssueCreateProvider } from '@atlassian/jira-business-issue-create/src/controllers/issue-create-context/index.tsx';
import {
	CREATE_ISSUE_MUTATION,
	ISSUE_REFETCH_QUERY,
} from '@atlassian/jira-business-list/src/services/create-issue/gql.tsx';
import { WorkflowsProvider } from '@atlassian/jira-business-workflows/src/controllers/workflows-context/index.tsx';
import LoginRedirect from '@atlassian/jira-common-navigation/src/login-redirect';
import { AuthenticationErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/authentication-error-boundary/index.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { STATUS_TYPE } from '@atlassian/jira-platform-field-config';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import type { formatRulesQuery as FormattingRulesQuery } from '@atlassian/jira-relay/src/__generated__/formatRulesQuery.graphql';
import { VIEW_EXPERIENCE } from './common/constants';
import { useJWMListFeatures } from './controllers/features-context';
import { useGroupBy } from './controllers/group-by';
import { useCreateIssue } from './services/create-issue';
import ListView from './ui/list-view';

const analyticsSourceName = 'listView';

export type ListViewProps = {
	header?: ReactNode;
	formattingRulesQuery?: PreloadedQuery<FormattingRulesQuery> | null;
};

export const ListViewRoot = memo<ListViewProps>(({ header, formattingRulesQuery }) => {
	const { isSoftwareFieldsEnabled } = useJWMListFeatures();
	const projectData = useProject();
	const projectId = String(projectData.id);
	const projectKey = projectData.key;
	const projectName = projectData.name;
	const isTeamManaged = projectData.isSimplified;
	const [groupByFieldId] = useGroupBy();
	const isGroupingByStatus = groupByFieldId === STATUS_TYPE;
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const services = fg('use-generated-types-for-create-issue') ? useCreateIssue() : undefined;

	const ConditionalWorkflowProvider =
		fg('jwm_conditional_workflow_data_loading') || isGroupingByStatus
			? WorkflowsProvider
			: Fragment;

	const content = (
		<ContextualAnalyticsData objectType="list" sourceType={SCREEN} sourceName={analyticsSourceName}>
			<ListView
				projectKey={projectKey}
				projectName={projectName}
				projectId={projectId}
				isTeamManaged={isTeamManaged}
				formattingRulesQuery={formattingRulesQuery}
			/>
			<FireScreenAnalytics attributes={{ projectKey }} />
		</ContextualAnalyticsData>
	);

	return (
		<BusinessAppWrapper
			header={header}
			sidebarTheme={isSoftwareFieldsEnabled ? 'software' : 'business'}
			withSidebar
			withOverflow
		>
			<IssueTypesAndFieldsContext>
				<ConditionalWorkflowProvider enabled={isGroupingByStatus}>
					<IssueCreateProvider
						mutation={CREATE_ISSUE_MUTATION}
						query={ISSUE_REFETCH_QUERY}
						onCreateIssue={services?.createIssue}
						onRefetchIssue={services?.refetchIssue}
					>
						{content}
					</IssueCreateProvider>
				</ConditionalWorkflowProvider>
				<RefetchFieldsController />
			</IssueTypesAndFieldsContext>
		</BusinessAppWrapper>
	);
});

const ListViewRootWithConfluenceProvider = ({ children }: { children: React.ReactElement }) => {
	const hasConfluence = useTenantHasConfluence();

	return hasConfluence ? <ConfluenceModalProvider>{children}</ConfluenceModalProvider> : children;
};

export const ListViewRootWithSmartCard = (props: ListViewProps) => {
	const hasConfluence = useTenantHasConfluence();

	// `hasConfluence` check only needed until we roll out `jsw_perf_format-links-in-custom-fields` experiment.
	// remove this condition after clean up but keep `SmartCardProvider` & `ListViewRootWithConfluenceProvider`
	if (hasConfluence || expValEquals('jsw_perf_format-links-in-custom-fields', 'list-view', true)) {
		return (
			<SmartCardProvider>
				<ListViewRootWithConfluenceProvider>
					<ApolloClientProvider client={client}>
						<ListViewRoot {...props} />
					</ApolloClientProvider>
				</ListViewRootWithConfluenceProvider>
			</SmartCardProvider>
		);
	}
	return (
		<ApolloClientProvider client={client}>
			<ListViewRoot {...props} />
		</ApolloClientProvider>
	);
};
const List = (props: ListViewProps) => (
	<ExperienceErrorBoundary experience={VIEW_EXPERIENCE} shouldStartExperience>
		{fg('jira_list_add_401_error_handling') ? (
			<AuthenticationErrorBoundary render={() => <LoginRedirect />}>
				<ListViewRootWithSmartCard {...props} />
			</AuthenticationErrorBoundary>
		) : (
			<ListViewRootWithSmartCard {...props} />
		)}
	</ExperienceErrorBoundary>
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default memo<Record<any, any>>(List);
